import React, { useState, useEffect } from "react";
import { PrimaryButton, ButtonSizeOptions, InputNumber, InputTypeAhead } from "@loanbossinc/component-library";
import { withRouter } from "react-router-dom";
import { LBFooter } from "components";
import { connect } from "react-redux";
import { compose } from "redux";
import { mapCompanySelector } from "store/state/auth";
import { BASE_INDEXES_FIXED, FLOATING_INDICES, SPREAD_TYPE, FIRST_PERIOD_ACCRUALS, DISCOUNT_METHOD } from "constants/configItemParents";
import { floatingIndexList } from "constants/floatingIndexes";

import { getDebtValuation, saveDebtValuation, getDebtValuationConfigItems } from "./debtValuation.store";
import { PageContainer, ImageBanner, StyledImgLoader, DebtValuationParamsContainer, DebtValuationParamsWrapper, ContentWrapper, TitleWrapper, SectionWrapper, Title, SubTitle, SectionTitle, ControllsWrapper, Divider, ButtonWrapper, FooterContainer, StyledButtonText } from "./styledComponents";

const defaultImageUrl = process.env.PUBLIC_URL + "/images/default-banner.png";

const DebtValuationSetup = ({ company, debtValuation, getDebtValuation, getDebtValuationConfigItems, saveDebtValuation, configItems, history }) => {
  const [debtValuationData, setDebtValuationData] = useState(null);
  const [floatingRateIndexes, setFloatingRateIndexes] = useState([]);

  useEffect(() => {
    getDebtValuation();
    getDebtValuationConfigItems();
    setFloatingRateIndexes(floatingIndexList
      .filter(({ name }) => name !== "None")
      .map(({ name, value }) => ({ id: value, name }))
    );
  }, []);

  useEffect(() => {
    setDebtValuationData(debtValuation);
  }, [debtValuation]);

  const handleChange = (name, value) => {
    const changed = { ...debtValuationData };
    changed[name] = value;

    if (name === "spreadTypeId" && value === "3f5dd400-a94c-43cb-b07d-da239de64482") {
      changed.singleSpreadValue = null;
    }

    setDebtValuationData(changed);
  };

  const handleSave = () => {
    saveDebtValuation(debtValuationData);
  };

  if (!debtValuationData) {
    return null;
  }

  return (
    <PageContainer>
      <ImageBanner>
        <StyledImgLoader
          src={company && company.bannerImage}
          defaultImage={defaultImageUrl}
          height="320px"
          width="100%"
          enableChangeDialog={false}
          changeButtonText={company && company.bannerImage ? "Change banner" : "Add banner"}
          changeDialogTitle={company && company.bannerImage ? "Change cover image" : "Add cover image"}
          changeDialogMessage="Images wider than 1500 pixels work best."
          onImageRemove={() => { }}
          onImageChange={() => { }}
        />
      </ImageBanner>
      <DebtValuationParamsContainer>
        <DebtValuationParamsWrapper>
          <ContentWrapper>
            <TitleWrapper>
              <Title>Set up your Debt Valuation</Title>
              <SubTitle>Select your default settings</SubTitle>
            </TitleWrapper>
            <SectionWrapper>
              <SectionTitle>Fixed Rate Loans</SectionTitle>
              <ControllsWrapper>
                <InputTypeAhead
                  id="fixedDiscountMethodId"
                  name="fixedDiscountMethodId"
                  options={configItems.discountMethods}
                  value={debtValuationData.fixedDiscountMethodId}
                  label="Discount Method"
                  onChange={e => handleChange("fixedDiscountMethodId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />
                {(!debtValuationData.fixedDiscountMethodId || debtValuationData.fixedDiscountMethodId === "9f546429-fd47-4fa4-bdf2-35e4bc9b2435") && <InputTypeAhead
                  id="fixedBaseIndexId"
                  name="fixedBaseIndexId"
                  options={configItems.fixedIndexes}
                  value={debtValuationData.fixedBaseIndexId}
                  label="Discount Base"
                  onChange={e => handleChange("fixedBaseIndexId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />}
                {debtValuationData.fixedDiscountMethodId === "7f236153-36a7-49f8-9124-1ccae71fc363" && <InputTypeAhead
                  id="fixedBaseIndexId"
                  name="fixedBaseIndexId"
                  options={floatingRateIndexes}
                  value={debtValuationData.fixedBaseIndexId}
                  label="Discount Base"
                  onChange={e => handleChange("fixedBaseIndexId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />}
                <InputTypeAhead
                  id="fixedFirstPeriodAccrualsId"
                  name="fixedFirstPeriodAccrualsId"
                  options={configItems.firstPeriodAccrualTypes}
                  value={debtValuationData.fixedFirstPeriodAccrualsId}
                  label="First Period Accruals"
                  onChange={e => handleChange("fixedFirstPeriodAccrualsId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />
              </ControllsWrapper>
            </SectionWrapper>
            <Divider />
            <SectionWrapper>
              <SectionTitle>Floating Rate Loans</SectionTitle>
              <ControllsWrapper>
                <InputTypeAhead
                  id="floatingDiscountMethodId"
                  name="floatingDiscountMethodId"
                  options={configItems.discountMethods}
                  value={debtValuationData.floatingDiscountMethodId}
                  label="Discount Method"
                  onChange={e => handleChange("floatingDiscountMethodId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />

                {(!debtValuationData.floatingDiscountMethodId || debtValuationData.floatingDiscountMethodId === "9f546429-fd47-4fa4-bdf2-35e4bc9b2435") && <InputTypeAhead
                  id="floatingBaseIndexId"
                  name="floatingBaseIndexId"
                  options={configItems.fixedIndexes}
                  value={debtValuationData.floatingBaseIndexId}
                  label="Discount Base"
                  onChange={e => handleChange("floatingBaseIndexId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />}
                {debtValuationData.floatingDiscountMethodId === "7f236153-36a7-49f8-9124-1ccae71fc363" && <InputTypeAhead
                  id="floatingBaseIndexId"
                  name="floatingBaseIndexId"
                  options={floatingRateIndexes}
                  value={debtValuationData.floatingBaseIndexId}
                  label="Discount Base"
                  onChange={e => handleChange("floatingBaseIndexId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />}

                <InputTypeAhead
                  id="floatingFirstPeriodAccrualsId"
                  name="floatingFirstPeriodAccrualsId"
                  options={configItems.firstPeriodAccrualTypes}
                  value={debtValuationData.floatingFirstPeriodAccrualsId}
                  label="First Period Accruals"
                  onChange={e => handleChange("floatingFirstPeriodAccrualsId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />
              </ControllsWrapper>
            </SectionWrapper>
            <Divider />
            <SectionWrapper>
              <SectionTitle>Spread Settings</SectionTitle>
              <ControllsWrapper>
                <InputTypeAhead
                  id="spreadTypeId"
                  name="spreadTypeId"
                  value={debtValuationData.spreadTypeId}
                  label="Spread Type"
                  options={configItems.spreadTypes}
                  onChange={e => handleChange("spreadTypeId", e)}
                  displayWith={option => option && option.name}
                  setValueWith={option => option && option.id}
                  variant="filled"
                  className="input"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />
                {debtValuationData.spreadTypeId === "ae2be7c9-83ca-4a5b-ab20-250e91a72b89" && <InputNumber
                  id="singleSpreadValue"
                  name="singleSpreadValue"
                  label="Calculation Spread (%)"
                  value={debtValuationData.singleSpreadValue ? debtValuationData.singleSpreadValue * 100 : null}
                  onChange={e => handleChange("singleSpreadValue", e.target.value / 100)}
                  fullWidth
                  decimals={2}
                  variant="filled"
                  style={{ width: "200px", marginLeft: "5px", marginRight: "5px" }}
                />}
              </ControllsWrapper>
            </SectionWrapper>
            <ButtonWrapper>
              <StyledButtonText label={"Back"} iconName="arrow_forward" onClick={history.goBack} reverse />
              <StyledButtonText label={"SingleSpread Table"} iconName="toc" onClick={() => history.push("/debtvaluationSpreads")} reverse />
              <PrimaryButton
                style={{ width: "132px" }}
                label="Save"
                onClick={handleSave}
                size={ButtonSizeOptions.smFluid}
              />
            </ButtonWrapper>
          </ContentWrapper>
        </DebtValuationParamsWrapper>
      </DebtValuationParamsContainer>
      <FooterContainer>
        <LBFooter />
      </FooterContainer>
    </PageContainer>
  );
};

const mapStateToProps = ({ authentication, debtValuation }) => ({
  authentication,
  company: mapCompanySelector(authentication),
  debtValuation: debtValuation.debtValuationSetup,
  configItems: {
    discountMethods: debtValuation.configItems[DISCOUNT_METHOD],
    fixedIndexes: debtValuation.configItems[BASE_INDEXES_FIXED],
    firstPeriodAccrualTypes: debtValuation.configItems[FIRST_PERIOD_ACCRUALS],
    floatingIndexes: debtValuation.configItems[FLOATING_INDICES],
    spreadTypes: debtValuation.configItems[SPREAD_TYPE]
  }
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    getDebtValuation,
    saveDebtValuation,
    getDebtValuationConfigItems
  })
)(DebtValuationSetup);
